import { createContext, useContext, useState } from 'react'
import { useAppBridge } from '@shopify/app-bridge-react'
import { genericMerchantErrorMessage, resolveMerchantErrorMessage } from '../../merchantErrors'
import type { HomeApi } from './api'
import { trackShopifyAction } from '../../shopifyUiTracking'
import type { HomePrefs, LoadState } from './types'
import { buildShopifyAppScopeKey, useShopifyAppContext } from '../../ShopifyAppContext'
import { useShopifyScopeLifecycle } from '../../hooks/useShopifyScopeLifecycle'

export interface HomeEnv {
  state: LoadState
  api: HomeApi
  /** In-app navigation. */
  navigate: (to: string) => void
}
export const HomeEnvContext = createContext<HomeEnv | null>(null)
const useEnv = () => {
  const env = useContext(HomeEnvContext)
  if (!env) throw new Error('Wrap the app home page in <HomeEnvContext.Provider>')
  return env
}

export type Busy = 'connect' | 'sync' | 'resync' | 'retry' | null

/**
 * Home actions and local busy state. HomeRoute derives the presentation directly
 * from scoped queries; there is no second server-data cache, retry loop or timer.
 */
export function useHome() {
  const { state, api, navigate } = useEnv()
  const shopify = useAppBridge()
  const scope = useShopifyAppContext()
  const { token, isActive } = useShopifyScopeLifecycle(buildShopifyAppScopeKey(scope))
  const [pending, setPending] = useState<{ token: string; kind: Busy } | null>(null)
  const busy = pending?.token === token ? pending.kind : null

  const actions = {
    retryLoad: async () => {
      setPending({ token, kind: 'retry' })
      try {
        await trackShopifyAction(shopify, 'retry-load', () => api.refresh())
      } catch (cause) {
        console.error(cause)
        if (isActive(token))
          shopify.toast.show(resolveMerchantErrorMessage(cause, genericMerchantErrorMessage()), { isError: true })
      } finally {
        if (isActive(token)) setPending(null)
      }
    },
    // Success navigates away; busy only clears on a failure to start the redirect.
    connect: () => {
      setPending({ token, kind: 'connect' })
      api.connect().catch((e) => {
        console.error(e)
        if (!isActive(token)) return
        setPending(null)
        shopify.toast.show(resolveMerchantErrorMessage(e, genericMerchantErrorMessage()), { isError: true })
      })
    },
    /** Shopify's pricing page opens in the admin. The merchant comes back to this page after choosing. */
    choosePlan: () => {
      void trackShopifyAction(shopify, 'choose-plan', () => api.choosePlan()).catch((cause) => {
        if (isActive(token))
          shopify.toast.show(resolveMerchantErrorMessage(cause, genericMerchantErrorMessage()), { isError: true })
      })
    },
    startSync: async () => {
      setPending({ token, kind: 'sync' })
      try {
        await api.startSync()
      } catch (cause) {
        // The scoped sync mutation owns failure feedback and tracking.
        console.error(cause)
      } finally {
        if (isActive(token)) setPending(null)
      }
    },
    resync: async () => {
      setPending({ token, kind: 'resync' })
      try {
        await api.resync()
      } catch (cause) {
        console.error(cause)
      } finally {
        if (isActive(token)) setPending(null)
      }
    },
    savePrefs: (patch: Partial<HomePrefs>) => {
      void trackShopifyAction(shopify, 'save-preferences', () => api.savePrefs(patch)).catch((cause) => {
        console.error('HOME_PREFERENCES_SAVE_FAILED', cause)
      })
    },
    dismissBanner: (id: string) => {
      if (state.kind !== 'ready') return
      actions.savePrefs({ dismissedBanners: [...state.data.prefs.dismissedBanners, id] })
    },
    newTest: (kind?: 'product' | 'store') =>
      trackShopifyAction(shopify, 'create-test', () =>
        navigate(kind ? `/app/tests/new?kind=${kind}` : '/app/tests/new'),
      ),
    go: (to: string) => trackShopifyAction(shopify, 'open-capability', () => navigate(to)),
  }

  return { state, busy, actions }
}

export type HomeActions = ReturnType<typeof useHome>['actions']
